import { useNavigate, useParams } from 'react-router-dom'
import { Cama, Habitacion, ModalidadDePago, TipoDescuento, TiposAlojamientos, TiposPagos, useAgregarExtrasEstanciaMutation, useGetHabitacionExtensionEstanciaQuery } from 'src/gql/schema'
import Screen from 'src/shared/components/layout/screen/Screen'
import Principal from './sections/Principal/Principal'
import { FormProvider, useForm } from 'react-hook-form'
import Personas from './sections/Personas/Personas'
import Pago from './sections/Pago/Pago'
import Propina from './sections/Propina/Propina'
import RenovacionResumen from './sections/RenovacionResumen/RenovacionResumen'
import './ExtensionEstancia.css'
import { CostsProvider, useCosts } from './context/CostContext'
import { useProfile } from 'src/shared/hooks/useProfile'
import { ExtensionEstanciaValues } from './EstensionEstancia-types'
import { div, sum, times } from 'src/shared/helpers/calculator'
import useModalAuthExtensionEstancia from './sections/Principal/sections/ModalAuth/ModalAuth'
import { RoleNames } from 'src/shared/hooks/useAuth'
import ModalDescuentos, { useStateDescuentos } from 'src/shared/modals/ModalDescuentos/ModalDescuentos'
import useMiniSnackbar from 'src/shared/hooks/useMiniSnackbar'
import useSnackbar from 'src/shared/hooks/useSnackbar'
import useLovePointsValidator from './hooks/useLovePointsValidator'
import { useEffect, useMemo } from 'react'
import { useDate } from 'src/shared/hooks/useDate'
import { addDays } from 'src/shared/helpers/addDays'
import { addExtraHours } from 'src/shared/helpers/addExtraHours'
import { PAYMENT_METHODS } from 'src/constants/payments'

const FORM_ID = "renovacion-form"

const ExtensionEstanciaWrapper = () => {
    const { habitacion_id = "" } = useParams()
    const { hotel_id, usuario_id, rolName } = useProfile()
    const methods = useForm<ExtensionEstanciaValues>()

    const { UTCStringToLocalDate, localDateToUTCString } = useDate()

    const pagos = methods.watch("pago.items")
    const paymentMethod = methods.watch("pago.mainMethod")
    const hospedajesExtra = methods.watch("principal.nochesExtraCount")
    const horasExtra = methods.watch("principal.horasExtraCount")
    const personasExtra = methods.watch("personas.extraCount")

    const { Modal: ModalNotEnoughLovePoints, setisModalOpen } = useLovePointsValidator({
        idMembresia: pagos?.find(p => p.easyReward)?.easyReward?.id,
        saldoDisponible: pagos?.find(p => p.easyReward)?.easyReward?.saldo
    })
    const { data, loading } = useGetHabitacionExtensionEstanciaQuery({
        variables: {
            habitacion_id
        }
    })
    const habitacion = data?.habitacion
    const { showMiniSnackbar } = useMiniSnackbar()
    const { showSnackbar } = useSnackbar()
    const navigate = useNavigate()
    const { costs } = useCosts()

    const [agregarEstancia] = useAgregarExtrasEstanciaMutation()
    const { descuento, setDescuento, metodos_sin_descuento } = useStateDescuentos()
    const fetch_descuento = habitacion?.ultima_renta?.descuento_aplicado

    const { tarifaBaseHabitacion } = useCosts()

    useEffect(() => {
        if (paymentMethod && descuento.descuento_id && metodos_sin_descuento.includes(paymentMethod as TiposPagos)) {
            setDescuento({ descuento_id: "", visible: false, total: 0, nombre: "" })
        }
    }, [paymentMethod, descuento.descuento_id])

    useEffect(() => {
        if (!hospedajesExtra && !horasExtra && !personasExtra && descuento.descuento_id) {
            setDescuento({ descuento_id: "", visible: false, total: 0, nombre: "" })
            return
        }
        if (descuento.tipo_descuento === TipoDescuento.Porcentaje && descuento.valor && costs.baseTotal) {
            setDescuento(prev => ({ ...prev, total: div(times(costs.baseTotal, (descuento.valor || 0)), 100) }))
        }
    }, [costs.baseTotal])

    const fecha_fin = useMemo(() => {
        const fecha_inicio = UTCStringToLocalDate(habitacion?.ultima_renta?.fecha_condensada)
        if (habitacion?.ultima_renta?.tipo_alojamiento === TiposAlojamientos.Hotel) {
            const fecha_fin = addDays({ date: fecha_inicio, days: hospedajesExtra || 0 })
            return fecha_fin
        }

        const fecha_fin = addExtraHours(fecha_inicio, times((hospedajesExtra || 0), tarifaBaseHabitacion?.duracion_renta || 0))
        return fecha_fin
    }, [habitacion, hospedajesExtra, tarifaBaseHabitacion])

    const confirmSubmit = (data: ExtensionEstanciaValues) => {
        const colaboradorPropinaId = data.pago.items.find(p => p.propina?.colaboradorPropina)?.propina?.colaboradorPropina?.id || null

        if (pagos.length === 1 && pagos.find(p => p.easyReward)?.easyReward?.id) {
            return setisModalOpen(true)
        }

        agregarEstancia({
            variables: {
                input: {
                    hotel_id,
                    ...(descuento.descuento_id ? { descuento_id: descuento.descuento_id } : {}),
                    cantidad_horas_extras: data.principal.horasExtraCount || 0,
                    cantidad_noches_extras: data.principal.nochesExtraCount || 0,
                    cantidad_personas_extras: data.personas.extraCount || 0,
                    renta_id: habitacion?.ultima_renta?.renta_id || "",
                    tarifas_ids: [...data.principal.tarifaId, ...data.principal.tarifasAdicionalesIds],
                    usuario_id,
                    ...(data.pago.type === ModalidadDePago.Total ? {
                        propina: {
                            detalles_pago: data.pago.items.map(p => ({
                                easyrewards_id: p.easyReward?.id || null,
                                numero_referencia: p.method === TiposPagos.DepositoOTransferencia ? p.number : null,
                                subtotal: Number(p.propina?.monto) || (
                                    p.propina?.porcentaje && p.propina.porcentaje !== "Otro"
                                        ? times(costs.totalConIva, Number(p.propina.porcentaje))
                                        : 0
                                ),
                                tipo_pago: p.method,
                                ultimos_digitos: [TiposPagos.Amex, TiposPagos.VisaOMastercard].includes(p.method) ? p.number : null
                            })),
                            colaborador_id: colaboradorPropinaId
                        }
                    } : {}),
                    ...(data.pago.type === ModalidadDePago.Total ? {
                        pago: {
                            detallesPago: data.pago.items.map(p => ({
                                easyrewards_id: p.easyReward?.id || null,
                                numero_referencia: p.method === TiposPagos.DepositoOTransferencia ? p.number : null,
                                subtotal: p.amount,
                                tipo_pago: p.method,
                                ultimos_digitos: [TiposPagos.Amex, TiposPagos.VisaOMastercard].includes(p.method) ? p.number : null
                            })),
                            hotel_id,
                            total: sum(data.pago.items.map(p => p.amount) || []),
                            usuario_id
                        }
                    } : {})
                }
            }
        }).then(() => {
            navigate(-1)
            showMiniSnackbar({
                status: 'success',
                title: "Estancia extendida",
                text: `Se ha registrado un **${data.pago.type === ModalidadDePago.Total ? "pago" : "pago pendiente"} de ${costs.totalConPropinas}** para la **renovación de la ${habitacion?.tipo_habitacion?.nombre || "-"} ${habitacion?.numero_habitacion}.**`
            })
        }).catch(() => {
            showSnackbar({
                status: 'error',
                title: "Error al renovar estancia"
            })
        })
    }

    const { Modal, setisAuthModalOpen } = useModalAuthExtensionEstancia({ onConfirm: () => confirmSubmit(methods.getValues()) })

    const onSubmit = (data: ExtensionEstanciaValues) => {
        const nochesExcedidas = habitacion?.tipo_habitacion?.hospedajes_extra_max !== null &&
            (habitacion?.tipo_habitacion?.hospedajes_extra_max || 0) - (habitacion?.ultima_renta?.hospedajes_extra || 0) < (data.principal.nochesExtraCount || 0)

        const horasExcedidas = habitacion?.tipo_habitacion?.horas_extra_max !== null &&
            (habitacion?.tipo_habitacion?.horas_extra_max || 0) - (habitacion?.ultima_renta?.horas_extra || 0) < (data.principal.horasExtraCount || 0)

        const personasExcedidas = habitacion?.tipo_habitacion?.personas_extra_max !== null &&
            (habitacion?.tipo_habitacion?.personas_extra_max || 0) - (habitacion?.ultima_renta?.personas_extra || 0) < (data.personas.extraCount || 0)

        if ((nochesExcedidas || horasExcedidas || personasExcedidas) && ![RoleNames.admin, RoleNames.gerente, RoleNames.superadmin].includes(rolName as RoleNames)) {
            return setisAuthModalOpen(true)
        }

        confirmSubmit(data)
    }

    return (
        <Screen
            close
            title={`${data?.habitacion.ultima_renta?.tipo_alojamiento === TiposAlojamientos.Hotel ? "Renovación estancia" : "Horas extra"} - ${habitacion?.tipo_habitacion?.nombre || ""} ${habitacion?.numero_habitacion || ""}`}
        >
            <FormProvider {...methods}>
                <form className="extension-estancia__sections" onSubmit={methods.handleSubmit(onSubmit)} id={FORM_ID}>
                    <div className="extension-estancia__sections__form">
                        <Principal
                            loading={loading}
                            tipo_alojamiento={data?.habitacion.ultima_renta?.tipo_alojamiento}
                            tipo_habitacion_camas={data?.habitacion.tipo_habitacion?.camas as Cama[]}
                            tipo_habitacion_id={data?.habitacion.tipo_habitacion_id}
                            tipo_habitacion_nombre={data?.habitacion.tipo_habitacion?.nombre}
                            tipo_habitacion_personas_incluidas={data?.habitacion.tipo_habitacion?.personas_incluidas}
                            habitacion={habitacion as Habitacion}
                        />
                        <Personas
                            loading={loading}
                            habitacion={habitacion as Habitacion}
                        />
                        <Pago
                            loading={loading}
                        />
                        <Propina
                            loading={loading}
                        />
                    </div>

                    <RenovacionResumen descuentoDesactivado={metodos_sin_descuento.includes(paymentMethod as any)}  descuento_unico={fetch_descuento?.descuento?.uso_unico || false} formId={FORM_ID} habitacion={habitacion as Habitacion}
                        onOpenDescuento={() => setDescuento({ ...descuento, visible: true })}
                        descuento={{ nombre: descuento.nombre, total: descuento.total, tipo_descuento: descuento.tipo_descuento, valor: descuento.valor }}
                    />
                </form>
            </FormProvider>
            {Modal}
            {descuento.visible && (
                <ModalDescuentos
                    filter={{
                        type: "renta",
                        tipo_habitacion_ids: habitacion?.tipo_habitacion_id ? [habitacion?.tipo_habitacion_id] : [],
                        grupo_descuento: habitacion?.ultima_renta?.tipo_alojamiento,
                        fecha_inicio: habitacion?.ultima_renta?.fecha_condensada,
                        fecha_fin: fecha_fin ? localDateToUTCString(fecha_fin) : "",
                        total: costs.baseTotal,
                        unique_discount_ids: [
                            ...(fetch_descuento?.descuento_id ? [fetch_descuento.descuento_id] : []),
                            ...(habitacion?.ultima_renta?.extras?.flatMap(e =>
                                e.descuento_aplicado?.descuento_id ? [e.descuento_aplicado.descuento_id] : []
                            ) || [])
                        ],
                    }}
                    title={"Descuentos de estancia"}
                    description={"Los descuentos se aplicarán al total de la estancia"}
                    onChange={(v) => {
                        setDescuento({ descuento_id: v.descuento_id, total: v.total, nombre: v.nombre, tipo_descuento: v.tipo_descuento, valor: v.valor, visible: false })
                        if (paymentMethod === PAYMENT_METHODS.mixto.value) {
                            methods.setValue("pago.items", [])
                            methods.setValue("pago.mainMethod", "")
                        }
                    }}
                    onClose={() => setDescuento({ ...descuento, visible: false })}
                    value={{ descuento_id: descuento.descuento_id, total: descuento.total, nombre: descuento.nombre }}
                />
            )}
            {ModalNotEnoughLovePoints}
        </Screen >
    )
}

const ExtensionEstancia = () => {
    return <CostsProvider>
        <ExtensionEstanciaWrapper />
    </CostsProvider>
}

export default ExtensionEstancia